import './IssueMembershipModal.css';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';
import CustomerLookupCombobox from './CustomerLookupCombobox';
import { resolveThemeToken } from '../themeTokens';
import { useIssueMembership, useIssueWalkInMembership, useMembershipTypes } from '../queries';
import type { MembershipTypeRow, UserLookupRow } from '../api/membership.api';
import type { ApiError } from '../../../shared/api/apiError';

// ─── Helpers ─────────────────────────────────────────────────

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatPrice = (pricePaise: number | null | undefined): string => {
  const rupees = (pricePaise ?? 0) / 100;
  if (rupees <= 0) return 'Free';
  if (rupees >= 100000) return `₹${(rupees / 100000).toFixed(1).replace(/\.0$/, '')}L`;
  if (rupees >= 1000) return `₹${(rupees / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `₹${rupees.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatTierValidityLabel = (validityDays: number | null | undefined): string => {
  if (!validityDays || validityDays <= 0) return 'Valid for life';
  if (validityDays < 30) return `Valid ${validityDays} day${validityDays === 1 ? '' : 's'}`;
  if (validityDays < 365) {
    const months = Math.round(validityDays / 30);
    return `Valid ${months} month${months === 1 ? '' : 's'}`;
  }
  const years = Math.round(validityDays / 365);
  return `Valid ${years} year${years === 1 ? '' : 's'}`;
};

// ─── Component ───────────────────────────────────────────────

type Props = {
  open: boolean;
  onClose: () => void;
  /**
   * When provided, the modal is locked to issuing this membership type and
   * the internal tier picker is hidden. When omitted, the modal renders a
   * tier picker prefilled to the lowest active tier-level.
   */
  typeUuid?: string;
  onRequestRenewInstead?: () => void;
};

const IssueMembershipModal = ({ open, onClose, typeUuid, onRequestRenewInstead }: Props) => {
  const issueMutation = useIssueMembership();
  const walkInIssueMutation = useIssueWalkInMembership();
  const typesQuery = useMembershipTypes(open && !typeUuid);

  const [customer, setCustomer] = useState<UserLookupRow | null>(null);
  const [lookupState, setLookupState] = useState({
    input: '',
    normalizedPhone: '',
    isExactPhoneLookup: false,
    completePhoneNotFound: false,
  });
  const [walkInName, setWalkInName] = useState('');
  const [walkInEmail, setWalkInEmail] = useState('');
  const [startsAt, setStartsAt] = useState<string>(() => formatLocalDate(new Date()));
  const [note, setNote] = useState('');
  const [inlineError, setInlineError] = useState('');
  const [selectedTypeUuid, setSelectedTypeUuid] = useState('');

  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const firstFieldRef = useRef<HTMLSelectElement | HTMLInputElement | null>(null);

  // ─── Memoized derivations ───
  const activeTiers = useMemo<MembershipTypeRow[]>(
    () => (typesQuery.data?.items ?? []).filter((tier) => tier.isActive !== false),
    [typesQuery.data],
  );

  const effectiveTypeUuid = typeUuid ?? selectedTypeUuid;

  const selectedTier = useMemo<MembershipTypeRow | null>(() => {
    if (!effectiveTypeUuid) return null;
    return activeTiers.find((tier) => tier.membershipTypeUuid === effectiveTypeUuid) ?? null;
  }, [activeTiers, effectiveTypeUuid]);

  // ─── Effects ───

  // Default tier picker to the lowest active tier-level when no fixed
  // typeUuid was supplied. Memoized so the dep list is stable.
  useEffect(() => {
    if (!open || typeUuid || selectedTypeUuid) return;
    if (activeTiers.length === 0) return;
    const sorted = [...activeTiers].sort((a, b) => (a.tierLevel ?? 0) - (b.tierLevel ?? 0));
    setSelectedTypeUuid(sorted[0]!.membershipTypeUuid);
  }, [open, typeUuid, selectedTypeUuid, activeTiers]);

  // Re-prefill Start At with today on every open transition.
  useEffect(() => {
    if (open) setStartsAt(formatLocalDate(new Date()));
  }, [open]);

  // Reset all picker / customer / note state on close so a re-open is fresh.
  useEffect(() => {
    if (open) return;
    setSelectedTypeUuid('');
    setCustomer(null);
    setLookupState({ input: '', normalizedPhone: '', isExactPhoneLookup: false, completePhoneNotFound: false });
    setWalkInName('');
    setWalkInEmail('');
    setNote('');
    setInlineError('');
  }, [open]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Autofocus the first interactive field on open.
  useEffect(() => {
    if (!open) return;
    const target = firstFieldRef.current;
    if (target) {
      // small delay so the element is mounted + the modal animation has begun
      requestAnimationFrame(() => target.focus());
    }
  }, [open]);

  // ─── Handlers ───

  const handleLookupStateChange = useCallback((state: {
    input: string;
    normalizedPhone: string;
    isExactPhoneLookup: boolean;
    completePhoneNotFound: boolean;
  }) => {
    setLookupState((current) => {
      if (
        current.input === state.input
        && current.normalizedPhone === state.normalizedPhone
        && current.isExactPhoneLookup === state.isExactPhoneLookup
        && current.completePhoneNotFound === state.completePhoneNotFound
      ) {
        return current;
      }
      return state;
    });
    if (!state.completePhoneNotFound) {
      setWalkInName('');
      setWalkInEmail('');
    }
  }, []);

  const handleIssue = async () => {
    setInlineError('');
    if (!effectiveTypeUuid) {
      setInlineError('Pick a tier first.');
      return;
    }
    if (!customer?.userUuid) {
      setInlineError('Select a customer first.');
      return;
    }
    try {
      await issueMutation.mutateAsync({
        userUuid: customer.userUuid,
        typeUuid: effectiveTypeUuid,
        startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,
        note: note || undefined,
      });
      onClose();
    } catch (error) {
      const apiError = error as ApiError;
      const rawData = (apiError?.data || {}) as any;
      const errorCode = String(rawData?.code || rawData?.data?.code || '');
      if (errorCode === 'MEMBERSHIP_ACTIVE_EXISTS') {
        setInlineError('Membership already active for this customer.');
        if (onRequestRenewInstead) {
          onRequestRenewInstead();
        }
        return;
      }
      setInlineError('Unable to issue membership right now.');
    }
  };

  const handleWalkInIssue = async () => {
    setInlineError('');
    if (!effectiveTypeUuid) {
      setInlineError('Pick a tier first.');
      return;
    }
    const name = walkInName.trim();
    const email = walkInEmail.trim();
    if (!lookupState.completePhoneNotFound || !lookupState.normalizedPhone) {
      setInlineError('Enter a full 10-digit phone number first.');
      return;
    }
    if (!name) {
      setInlineError('Enter customer name.');
      return;
    }
    try {
      await walkInIssueMutation.mutateAsync({
        phone: lookupState.normalizedPhone,
        name,
        email: email || undefined,
        typeUuid: effectiveTypeUuid,
        startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,
        note: note || undefined,
      });
      onClose();
    } catch (error) {
      const apiError = error as ApiError;
      const rawData = (apiError?.data || {}) as any;
      const errorCode = String(rawData?.code || rawData?.data?.code || '');
      if (errorCode === 'MEMBERSHIP_ACTIVE_EXISTS') {
        setInlineError('Membership already active for this customer.');
        if (onRequestRenewInstead) {
          onRequestRenewInstead();
        }
        return;
      }
      setInlineError('Unable to create customer and issue membership right now.');
    }
  };

  // ─── Derived UI flags ───
  const canCreateWalkIn = lookupState.completePhoneNotFound && !customer;
  const isSubmitting = issueMutation.isPending || walkInIssueMutation.isPending;
  const submitLabel = canCreateWalkIn
    ? (walkInIssueMutation.isPending ? 'Creating…' : 'Create and issue')
    : (issueMutation.isPending ? 'Issuing…' : 'Issue');
  const submitDisabled =
    isSubmitting
    || !effectiveTypeUuid
    || (canCreateWalkIn ? !walkInName.trim() : !customer?.userUuid);
  const customerLabel = customer?.name?.trim()
    || (canCreateWalkIn && walkInName.trim())
    || (lookupState.normalizedPhone ? `phone ${lookupState.normalizedPhone}` : 'a customer');
  const summaryLine = selectedTier
    ? `Issuing ${selectedTier.name} to ${customerLabel}`
    : 'Pick a tier and customer to continue';

  if (!open) return null;

  const theme = selectedTier ? resolveThemeToken(selectedTier.themeColorToken) : null;

  return (
    <div
      className="issue-mship-modal__backdrop"
      role="presentation"
      onMouseDown={(event) => {
        // Only close when the backdrop itself is the click target (not a
        // child that bubbled).
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="issue-mship-modal__container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="issue-mship-modal-title"
      >
        <header className="issue-mship-modal__header">
          <div className="issue-mship-modal__title-block">
            <h2 id="issue-mship-modal-title" className="issue-mship-modal__title">Issue membership</h2>
            <p className="issue-mship-modal__subtitle">Add a new member to one of your tiers</p>
          </div>
          <button
            type="button"
            className="issue-mship-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </header>

        <div className="issue-mship-modal__body">
          {!typeUuid ? (
            <label className="issue-mship-modal__field">
              <span className="issue-mship-modal__label">Tier</span>
              <span className="issue-mship-modal__select-wrap">
                <select
                  ref={firstFieldRef as any}
                  className="issue-mship-modal__input issue-mship-modal__select"
                  aria-label="Tier"
                  value={selectedTypeUuid}
                  onChange={(event) => setSelectedTypeUuid(event.target.value)}
                >
                  <option value="" disabled>
                    {typesQuery.isLoading ? 'Loading tiers…' : 'Pick a tier'}
                  </option>
                  {[...activeTiers]
                    .sort((a, b) => (a.tierLevel ?? 0) - (b.tierLevel ?? 0))
                    .map((tier) => (
                      <option key={tier.membershipTypeUuid} value={tier.membershipTypeUuid}>
                        {tier.name}
                      </option>
                    ))}
                </select>
                <ChevronDown
                  size={14}
                  strokeWidth={2}
                  className="issue-mship-modal__select-chevron"
                  aria-hidden="true"
                />
              </span>
            </label>
          ) : null}

          {selectedTier && theme ? (
            <div
              className={`issue-mship-modal__tier-summary issue-mship-modal__tier-summary--${theme.textMode}`}
              style={{ background: theme.cardBackground }}
              data-testid="issue-mship-tier-summary"
            >
              <div className="issue-mship-modal__tier-summary-name">{selectedTier.name}</div>
              <div className="issue-mship-modal__tier-summary-meta">
                <span className="issue-mship-modal__tier-summary-price">{formatPrice(selectedTier.pricePaise)}</span>
                <span className="issue-mship-modal__tier-summary-sep">·</span>
                <span>{formatTierValidityLabel(selectedTier.validityDays)}</span>
              </div>
              {selectedTier.description ? (
                <p className="issue-mship-modal__tier-summary-desc">{selectedTier.description}</p>
              ) : null}
            </div>
          ) : null}

          <label className="issue-mship-modal__field">
            <span className="issue-mship-modal__label">Customer</span>
            {customer ? (
              <div className="issue-mship-modal__customer-pill">
                <span>{customer.name} · {customer.phone}</span>
                <button
                  type="button"
                  className="issue-mship-modal__customer-change"
                  onClick={() => setCustomer(null)}
                >
                  Change
                </button>
              </div>
            ) : (
              <CustomerLookupCombobox
                value={customer}
                onSelect={(selectedCustomer) => {
                  setCustomer(selectedCustomer);
                  setInlineError('');
                }}
                onLookupStateChange={handleLookupStateChange}
              />
            )}
          </label>

          {canCreateWalkIn ? (
            <div className="issue-mship-modal__walk-in">
              <label className="issue-mship-modal__field">
                <span className="issue-mship-modal__label">Customer name</span>
                <input
                  type="text"
                  className="issue-mship-modal__input"
                  value={walkInName}
                  onChange={(event) => setWalkInName(event.target.value)}
                  placeholder="Name"
                />
              </label>
              <label className="issue-mship-modal__field">
                <span className="issue-mship-modal__label">Email (optional)</span>
                <input
                  type="email"
                  className="issue-mship-modal__input"
                  value={walkInEmail}
                  onChange={(event) => setWalkInEmail(event.target.value)}
                  placeholder="customer@example.com"
                />
              </label>
            </div>
          ) : null}

          <label className="issue-mship-modal__field">
            <span className="issue-mship-modal__label">Starts at</span>
            <div className="issue-mship-modal__date-row">
              <input
                type="date"
                ref={dateInputRef}
                className="issue-mship-modal__input"
                value={startsAt}
                onChange={(event) => setStartsAt(event.target.value)}
              />
              <button
                type="button"
                className="issue-mship-modal__date-trigger"
                aria-label="Open calendar"
                onClick={() => {
                  const el = dateInputRef.current;
                  if (el && typeof el.showPicker === 'function') {
                    el.showPicker();
                  } else {
                    el?.focus();
                  }
                }}
              >
                <Calendar size={16} />
              </button>
            </div>
          </label>

          <label className="issue-mship-modal__field">
            <span className="issue-mship-modal__label">Note (optional)</span>
            <textarea
              rows={3}
              className="issue-mship-modal__input issue-mship-modal__input--textarea"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>

          {inlineError ? (
            <div className="issue-mship-modal__error">
              <p>{inlineError}</p>
              {inlineError.includes('already active') && onRequestRenewInstead ? (
                <button
                  type="button"
                  className="issue-mship-modal__error-action"
                  onClick={onRequestRenewInstead}
                >
                  Renew instead
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <footer className="issue-mship-modal__footer">
          <p className="issue-mship-modal__summary" data-testid="issue-mship-summary">{summaryLine}</p>
          <div className="issue-mship-modal__actions">
            <button
              type="button"
              className="issue-mship-modal__cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="issue-mship-modal__submit"
              onClick={canCreateWalkIn ? handleWalkInIssue : handleIssue}
              disabled={submitDisabled}
            >
              {submitLabel}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default IssueMembershipModal;
export {
  // exported for unit testing
  formatPrice,
  formatTierValidityLabel,
};
